import { appendPlanTags } from "./plan_tags";
import { type Cue, type TimelineNode } from "../../../model/manuals/lecture_video";

const renderTimeline = (
  root: TimelineNode,
  video: HTMLVideoElement,
  seekButtonsByStartTime: Map<number, HTMLButtonElement>,
): HTMLUListElement => _renderTree(root, seekButtonsByStartTime, video);

interface HeadingLabelTransformResult {
  text: string;
  /** セクション見出し用の太字スタイル（`Cue.isHeading` とは別: 表示上の修飾） */
  emphasizeSection: boolean;
}

const _formatStartTime = (totalSeconds: number): string => {
  const s = Math.floor(Math.max(0, totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};

const _createTimeSpan = (seconds: number): HTMLSpanElement => {
  const timeEl = document.createElement("span");
  timeEl.className = "lecture_timeline_time";
  timeEl.textContent = _formatStartTime(seconds);
  timeEl.setAttribute("aria-hidden", "true");
  return timeEl;
};

const _transformHeadingLabel = (cueTitle: string, cue: Cue): HeadingLabelTransformResult => {
  if (!cue.isHeading) {
    return { text: cueTitle, emphasizeSection: false };
  }

  const ord = cue.headingOrdinal;
  const lines = cueTitle.split("\n");
  const firstLine = lines.findIndex((line) => line.trim().length > 0);

  if (ord == null) {
    return {
      text: firstLine === -1 ? "" : lines.join("\n"),
      emphasizeSection: true,
    };
  }

  if (firstLine === -1) {
    return { text: `${ord}.`, emphasizeSection: true };
  }

  lines[firstLine] = `${ord}. ${lines[firstLine].trim()}`;
  return { text: lines.join("\n"), emphasizeSection: true };
};

const _createBranchHeading = (child: TimelineNode): HTMLDivElement => {
  const label = document.createElement("div");
  label.className = "lecture_timeline_heading";
  label.appendChild(_createTimeSpan(child.minStart));

  const textEl = document.createElement("span");
  textEl.className = "lecture_timeline_heading_text";
  textEl.textContent = child.segment;
  label.appendChild(textEl);

  return label;
};

const _createCueSeekButton = (
  cue: Cue,
  video: HTMLVideoElement,
  seekButtonsByStartTime: Map<number, HTMLButtonElement>,
): HTMLButtonElement => {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "lecture_timeline_link";
  btn.appendChild(_createTimeSpan(cue.startTime));

  const heading = _transformHeadingLabel(cue.cueTitle, cue);

  const titleSpan = document.createElement("span");
  titleSpan.className = "lecture_timeline_label";
  titleSpan.textContent = heading.text;
  if (heading.emphasizeSection) {
    titleSpan.classList.add("lecture_timeline_section_label");
  }
  btn.appendChild(titleSpan);
  appendPlanTags(btn, [...cue.planTags]);

  btn.dataset.startTime = String(cue.startTime);
  seekButtonsByStartTime.set(cue.startTime, btn);
  btn.addEventListener("click", () => {
    video.currentTime = cue.startTime;
    video.play().catch((err: unknown) => {
      if (!(err instanceof DOMException) || err.name !== "NotAllowedError") {
        console.warn("Failed to play lecture video after timeline click", err);
      }
    });
  });

  return btn;
};

const _renderTree = (
  node: TimelineNode,
  seekButtonsByStartTime: Map<number, HTMLButtonElement>,
  video: HTMLVideoElement,
): HTMLUListElement => {
  const ul = document.createElement("ul");
  ul.className = "lecture_timeline_list";

  [...node.children]
    .sort((a, b) => a.minStart - b.minStart)
    .forEach((child) => {
      const li = document.createElement("li");
      li.className = "lecture_timeline_item";

      if (child.children.length > 0) {
        li.appendChild(_createBranchHeading(child));
        li.appendChild(_renderTree(child, seekButtonsByStartTime, video));
      } else if (child.cue) {
        li.appendChild(
          _createCueSeekButton(child.cue, video, seekButtonsByStartTime),
        );
      }

      ul.appendChild(li);
    });

  return ul;
};

export { renderTimeline };
